import type { Board } from "../board";
import { DamageType } from "../damageType";
import { MovementEffect } from "../effects/movementEffect";
import { SmokeEffect } from "../effects/smokeEffect";
import {
	PRIORITY_LAST,
	type ProcessableThing,
	type OnAddToBoard,
	type SomethingWithState,
	type PostDeserializable,
} from "../interfaces";
import { Mob } from "../mob";
import { PhysicalThing } from "../physicalThing";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import { Player } from "../player";
import { randomChance } from "../random";
import { deserializeXY } from "../serialization";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Ashes } from "./ashes";
import { DeadPlant } from "./deadPlant";
import { Plant } from "./plant";
import { Spreader } from "./spreader";

export class Fire
	extends PhysicalThing
	implements ProcessableThing, OnAddToBoard, SomethingWithState, PostDeserializable
{
	static key = "Fire";
	private fuel: number;
	private attachedTo: PhysicalThing | null = null;
	private static readonly DEFAULT_FUEL = 5;
	private static readonly DAMAGE = 3;
	private static readonly MOB_DAMAGE = 3; // kills normal mobs in 4 turns
	private static readonly SPREAD_FUEL_RETAIN = 0.5;
	private static readonly TERRAIN_SPREAD_FUEL = Fire.DEFAULT_FUEL;

	constructor(xy: XY, fuel: number = Fire.DEFAULT_FUEL) {
		super(xy, false);
		this.fuel = fuel;
	}

	getZOrder(): number {
		return ZOrder.FIRE;
	}

	onAddToBoard(): void {
		// If already attached (e.g. from spreadToFlammableThings), skip auto-attachment
		if (!this.attachedTo) {
			const things = this.board!.getThingsAt(this.xy);
			// First pass: attach to flammable things (Plant, Spreader, Mob, DeadPlant)
			for (const thing of things) {
				if (thing === this) continue;
				if (Fire.isFlammable(thing)) {
					this.attachedTo = thing;
					break;
				}
			}
			// Second pass: attach to Player (doesn't provide fuel but can still burn)
			if (!this.attachedTo) {
				for (const thing of things) {
					if (thing === this) continue;
					if (thing instanceof Player) {
						this.attachedTo = thing;
						break;
					}
				}
			}
		}

		// Apply damage immediately on contact
		if (this.attachedTo instanceof PhysicalThingWithHp) {
			this.attachedTo.applyDamage(DamageType.FIRE, Fire.damageFor(this.attachedTo), this);
		}

		if (this.attachedTo instanceof Mob) {
			this.attachedTo.staggerMovement();
		}

		const existing = this.findMergeableFire();
		if (existing) {
			existing.fuel += this.fuel;
			this.board!.removeThing(this);
		}
	}

	private findMergeableFire(): Fire | null {
		for (const thing of this.board!.getThingsAt(this.xy)) {
			if (thing === this) continue;
			if (thing instanceof Fire && thing.active && thing.attachedTo === this.attachedTo) {
				return thing;
			}
		}
		return null;
	}

	getState(): string {
		if (
			this.attachedTo &&
			this.attachedTo.active &&
			(this.attachedTo instanceof Mob || this.attachedTo instanceof Player)
		) {
			return "attachedtothing";
		}
		return "ground";
	}

	postDeserialize(_board: Board): void {
		this.onAddToBoard();
	}

	processTurn(board: Board): void {
		this.fuel -= 1 + (Math.random() * 0.6 - 0.3);

		if (this.fuel <= 0) {
			board.removeAtEndOfTurn(this);
			board.addEffect(new SmokeEffect(this.xy));
			return;
		}

		// Any fire damages the player standing on it (unless already attached to the player)
		if (!(this.attachedTo instanceof Player)) {
			const player = board.getThingsAt(this.xy).find((t) => t instanceof Player);
			if (player) {
				player.applyDamage(DamageType.FIRE, Fire.DAMAGE, this);
			}
		}

		// Handle attachment
		if (this.attachedTo !== null) {
			if (this.attachedTo.active) {
				// Refuel from attached thing
				this.fuel += Fire.getSustainFuel(this.attachedTo);

				// Apply damage or convert DeadPlant to Ashes
				if (this.attachedTo instanceof PhysicalThingWithHp) {
					this.attachedTo.applyDamage(DamageType.FIRE, Fire.damageFor(this.attachedTo), this);
				} else if (this.attachedTo instanceof DeadPlant) {
					if (this.fuel < 2) {
						board.removeAtEndOfTurn(this.attachedTo);
						board.addThing(new Ashes(this.attachedTo.xy));
					}
				}

				// Follow attached thing if it moved
				if (!this.attachedTo.xy.equals(this.xy)) {
					board.addEffect(new MovementEffect(this, this.xy, this.attachedTo.xy));
					this.xy = this.attachedTo.xy;
				}
			} else {
				this.attachedTo = null;
			}
		}

		// Spread to current tile (always)
		this.spreadToFlammableThings(board, this.xy);

		// Spread to neighbors (50% chance each)
		for (const neighborXY of board.getNeighbors(this.xy)) {
			if (randomChance(0.5)) {
				this.spreadToFlammableThings(board, neighborXY);
			}
		}
	}

	private spreadToFlammableThings(board: Board, targetXY: XY): void {
		const thingsOnCell = board.getThingsAt(targetXY);

		if (thingsOnCell.length === 0) {
			// 5% chance to spawn ground fire on empty cells
			if (randomChance(0.05)) {
				this.spawnNewGroundFire(board, targetXY);
			}
			return;
		}

		// Collect things already being burned by existing fires on this cell
		const thingsBurningHere: PhysicalThing[] = [];
		for (const thing of thingsOnCell) {
			if (thing instanceof Fire) {
				const attachment = thing.getAttachment();
				if (attachment) thingsBurningHere.push(attachment);
			}
		}

		// Spread to flammable things not already burning
		for (const thing of [...thingsOnCell]) {
			// Don't set fire to this fire's own target
			if (thing === this.attachedTo) continue;

			if (!Fire.isFlammable(thing)) continue;

			// Only spread if not already burning
			if (thingsBurningHere.includes(thing)) continue;

			this.spawnNewFire(board, thing);
		}
	}

	private spawnNewFire(board: Board, target: PhysicalThing): void {
		const childFuel = Fire.isTerrainFlammable(target) ? Fire.TERRAIN_SPREAD_FUEL : this.fuel * Fire.SPREAD_FUEL_RETAIN;
		const fire = new Fire(target.xy, childFuel);
		fire.attachedTo = target;
		board.addThing(fire);
	}

	private spawnNewGroundFire(board: Board, xy: XY): void {
		const fire = new Fire(xy, this.fuel * Fire.SPREAD_FUEL_RETAIN);
		board.addThing(fire);
	}

	getAttachment(): PhysicalThing | null {
		return this.attachedTo;
	}

	private static isTerrainFlammable(thing: PhysicalThing): boolean {
		return thing instanceof Plant || thing instanceof Spreader || thing instanceof DeadPlant;
	}

	private static isFlammable(thing: PhysicalThing): boolean {
		return Fire.isTerrainFlammable(thing) || thing instanceof Mob;
	}

	private static damageFor(thing: PhysicalThing): number {
		return thing instanceof Mob ? Fire.MOB_DAMAGE : Fire.DAMAGE;
	}

	private static getSustainFuel(thing: PhysicalThing): number {
		if (thing instanceof Plant) return 1;
		if (thing instanceof Spreader) return 0.4;
		if (thing instanceof DeadPlant) return 0.1;
		return 0;
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), fuel: this.fuel };
	}

	static deserialize(data: any): Fire {
		const f = new Fire(deserializeXY(data.xy), data.fuel);
		f.visualId = data.visualId;
		return f;
	}
}
